import type { FormatGroup, ThemeTemplate } from "./types";

type TemplateElement = Record<string, unknown>;
type TemplateUnitInput = {
  kind?: unknown;
  elements?: unknown;
};
type TemplateContentInput = {
  meta?: { layout_mode?: unknown };
  pages?: unknown;
};

const FORMAT_PAGE_MM: Record<FormatGroup, { w: number; h: number }> = {
  a4_portrait: { w: 210, h: 297 },
  a4_landscape: { w: 297, h: 210 },
  a5_portrait: { w: 148, h: 210 },
  a5_landscape: { w: 210, h: 148 }
};

function cloneJson<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function asElement(value: unknown): TemplateElement | null {
  return value && typeof value === "object" ? (value as TemplateElement) : null;
}

function isTemplateElement(value: unknown): value is TemplateElement {
  return Boolean(value && typeof value === "object");
}

function elementString(el: TemplateElement, key: string) {
  return String(el[key] || "");
}

function countPhysicalPages(docData: TemplateContentInput) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  if (String(docData?.meta?.layout_mode || "pages") !== "book_spreads") {
    return pages.length;
  }

  return pages.reduce((sum: number, unit: unknown) => {
    const item = unit && typeof unit === "object" ? (unit as TemplateUnitInput) : {};
    return sum + (String(item.kind || "single") === "spread" ? 2 : 1);
  }, 0);
}

function isClipartImage(el: TemplateElement) {
  return elementString(el, "type") === "image" && elementString(el, "source") === "clipart" && Boolean(el.clipart_id);
}

function sanitizeImagePlaceholder(el: TemplateElement) {
  const next = cloneJson(el || {});
  delete next.asset_id;
  delete next.image_url;
  delete next.image_src;
  delete next.local_asset_path;
  delete next.source;
  delete next.clipart_id;
  delete next.pan_mm;
  delete next._ghost;
  delete next._ghost_key;

  return {
    ...next,
    type: "image",
    asset_id: null,
    placeholder: true,
    placeholder_kind: "image",
    image_mode: "frame",
    fit: next.fit === "contain" ? "contain" : "cover",
    scale: 1
  } satisfies TemplateElement;
}

function sanitizeElementForTemplate(value: unknown, options?: { skipTemplateTips?: boolean }): TemplateElement | null {
  const el = asElement(value);
  if (!el || el._ghost) return null;
  if (options?.skipTemplateTips && el.template_tip) return null;

  if (elementString(el, "type") === "image" && !isClipartImage(el)) {
    return sanitizeImagePlaceholder(el);
  }

  const next = cloneJson(el);
  delete next.image_src;
  delete next.local_asset_path;
  delete next._ghost;
  delete next._ghost_key;

  if (elementString(next, "type") === "image" && "image_url" in next && !isClipartImage(next)) {
    delete next.image_url;
  }

  return next;
}

function parseTipsByPhysicalPage(value?: string) {
  const out = new Map<number, string>();
  const lines = String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n");

  for (const line of lines) {
    const match = line.trim().match(/^(\d+(?:\s*,\s*\d+)*)\s*[:.)-]\s*(.+)$/);
    if (!match) continue;

    const text = match[2].trim().slice(0, 500);
    if (!text) continue;

    match[1]
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((pageNumber) => Number.isFinite(pageNumber) && pageNumber > 0)
      .forEach((pageNumber) => out.set(pageNumber, text));
  }

  return out;
}

function createTipTextElement(args: {
  text: string;
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
}) {
  return {
    id: `tip_${Math.random().toString(16).slice(2)}`,
    type: "text",
    text_role: "body",
    text: args.text,
    x_mm: args.xMm,
    y_mm: args.yMm,
    w_mm: args.wMm,
    h_mm: args.hMm,
    rotate_deg: 0,
    editor_only: true,
    exclude_from_print: true,
    template_tip: true,
    style: {
      font_family: "Raleway",
      font_size_pt: 9,
      font_weight: 600,
      line_height: 1.25,
      color_hex: "#334155",
      fill_hex: "rgba(255,255,255,0.88)",
      text_align: "left",
      padding_mm: 2
    }
  } satisfies TemplateElement;
}

function createTipTextElementsForUnit(args: {
  tipsByPage: Map<number, string>;
  physicalPageStart: number;
  unitKind: "single" | "spread";
  formatGroup: FormatGroup;
}) {
  const page = FORMAT_PAGE_MM[args.formatGroup] || FORMAT_PAGE_MM.a4_portrait;
  const marginMm = 8;
  const wMm = Math.min(58, Math.max(42, page.w * 0.28));
  const hMm = 22;
  const yMm = marginMm;
  const rightX = page.w - wMm - marginMm;
  const out: TemplateElement[] = [];

  if (args.unitKind === "spread") {
    const leftText = args.tipsByPage.get(args.physicalPageStart);
    const rightText = args.tipsByPage.get(args.physicalPageStart + 1);
    if (leftText) out.push(createTipTextElement({ text: leftText, xMm: rightX, yMm, wMm, hMm }));
    if (rightText) out.push(createTipTextElement({ text: rightText, xMm: page.w + rightX, yMm, wMm, hMm }));
    return out;
  }

  const text = args.tipsByPage.get(args.physicalPageStart);
  if (text) out.push(createTipTextElement({ text, xMm: rightX, yMm, wMm, hMm }));
  return out;
}

export function buildGeneratedThemeTemplate(args: {
  name: string;
  contentData: TemplateContentInput;
  formatGroup: FormatGroup;
  tipsText?: string;
}): ThemeTemplate {
  const { name, contentData, formatGroup } = args;
  const tipsByPage = parseTipsByPhysicalPage(args.tipsText);
  const replaceExistingTips = tipsByPage.size > 0;
  const pages = Array.isArray(contentData?.pages) ? contentData.pages : [];
  let physicalPageNumber = 1;

  return {
    id: "generated_pending",
    name,
    target_physical_pages: countPhysicalPages(contentData),
    format_group: formatGroup,
    source: "generated",
    snapshot_pages: pages.map((unit: unknown) => {
      const item = unit && typeof unit === "object" ? (unit as TemplateUnitInput) : {};
      const unitKind = String(item.kind || "single") === "spread" ? "spread" : "single";
      const physicalPageStart = physicalPageNumber;
      physicalPageNumber += unitKind === "spread" ? 2 : 1;
      const elements = (Array.isArray(item.elements) ? item.elements : [])
        .map((element) => sanitizeElementForTemplate(element, { skipTemplateTips: replaceExistingTips }))
        .filter(isTemplateElement);
      return {
        kind: unitKind,
        elements: [
          ...elements,
          ...createTipTextElementsForUnit({ tipsByPage, physicalPageStart, unitKind, formatGroup })
        ]
      };
    })
  };
}
